import React, { useState } from 'react';
import { Box, ButtonBase, Menu, MenuItem } from '@mui/material';
import { SERIF, SANS, MONO, todayTokens } from '../../theme/todayTokens';
import { safeFormatAddress } from '../../utils/addressUtils';
import { jobSteps, rentSummary, formatCurrency } from './propertyDetailsUtils';
import { isAlertsOnHold } from '../../utils/alertsOnHold';

/** Shared "Today" style primitives for the property record. */
export function recordStyles(isDarkMode) {
  const t = todayTokens(isDarkMode);
  const focusRing = { '&.Mui-focusVisible': { outline: `2px solid ${t.navy}`, outlineOffset: '2px' } };
  return {
    t,
    focusRing,
    card: { bgcolor: t.surface, border: `1px solid ${t.line}`, borderRadius: '18px' },
    h2: { m: 0, fontFamily: SERIF, fontWeight: 400, fontSize: { xs: 28, md: 32 }, lineHeight: 1, letterSpacing: '-0.01em', color: t.ink },
    eyebrow: { fontFamily: MONO, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: t.muted },
    textLink: { fontFamily: SANS, fontSize: 13, fontWeight: 600, color: t.navy, borderRadius: '6px', '&:hover': { color: t.rust }, ...focusRing },
    underlineLink: { fontFamily: SANS, fontSize: 14, fontWeight: 600, color: t.navy, borderRadius: '6px', minHeight: 44, textDecoration: 'underline', textUnderlineOffset: '3px', '&:hover': { color: t.rust }, '&.Mui-disabled': { opacity: 0.45, textDecoration: 'none' }, ...focusRing },
    outlineBtn: { minHeight: 44, px: 2, borderRadius: '10px', fontFamily: SANS, fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', bgcolor: t.surface, color: t.ink, border: `1px solid ${t.lineStrong}`, '&.Mui-disabled': { opacity: 0.45 }, ...focusRing },
  };
}

const toneMark = (t, tone) => ({
  ok: { radius: '50%', fill: t.green, stroke: t.green, fg: t.green },
  soon: { radius: '50%', fill: 'transparent', stroke: t.amber, fg: t.amberText },
  urgent: { radius: '2px', fill: t.rust, stroke: t.rust, fg: t.rust },
}[tone]);

const amount = (v) => (typeof v === 'number' ? v : parseFloat(String(v || '').replace(/[£,\s]/g, '')) || 0);
const personName = (p) => p?.name || `${p?.firstName || ''} ${p?.lastName || ''}`.trim();

function Pill({ bg, fg, dot, children }) {
  return (
    <Box component="span" sx={{ minHeight: 32, display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, borderRadius: '16px', bgcolor: bg, color: fg, fontSize: 13, fontWeight: 600 }}>
      <Box component="span" aria-hidden sx={{ width: 8, height: 8, boxSizing: 'border-box', flexShrink: 0, ...dot }} />
      {children}
    </Box>
  );
}

function PersonCard({ s, role, link, onLink, name, meta, facts, actions }) {
  const { t } = s;
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      minWidth: 0,
      py: { xs: 2.25, md: 2 },
      px: { xs: 2.5, md: 2.75 },
      borderRight: { md: `1px solid ${t.line}` },
      borderBottom: { xs: `1px solid ${t.line}`, md: 0 },
      '&:last-of-type': { borderRight: 0, borderBottom: 0 },
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box component="span" sx={s.eyebrow}>{role}</Box>
        {link && <ButtonBase onClick={onLink} sx={s.textLink}>{link}</ButtonBase>}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
        <Box component="span" sx={{ fontFamily: SERIF, fontSize: { xs: 24, md: 28 }, lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</Box>
        <Box component="span" sx={{ fontSize: 13, color: t.muted }}>{meta}</Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.5, pt: 1.75, borderTop: `1px solid ${t.lineSoft}` }}>
        {facts.map(([k, v]) => (
          <Box key={k} sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
            <Box component="span" sx={{ fontSize: 12, color: t.muted }}>{k}</Box>
            <Box component="span" sx={{ fontSize: 15, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(v)}>{v}</Box>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 2.5, mt: 'auto', pt: 0.25 }}>
        {actions.map((a) => (
          <ButtonBase key={a.label} disabled={!a.onClick && !a.href} {...(a.href ? { component: 'a', href: a.href } : { onClick: a.onClick })} sx={{ ...s.underlineLink, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 12 }}>
            {a.label}
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
}

/** Hero, "needs you" strip, people/money cards and section tabs for the property record. */
export function PropertyRecordHeader({
  property, isDarkMode, userRole, attention, certRows, openJobs, payments, financeEnabled,
  tabs, tab, onTab, onBack, onEdit, onCompliance, onAddNote, onLogRepair, onAttention, onToggleHold,
  onRecordPayment, onViewPayments, onOpenTenant, onOpenLandlord,
}) {
  const s = recordStyles(isDarkMode);
  const { t } = s;
  const [menuEl, setMenuEl] = useState(null);

  const tenants = property?.tenants || [];
  const tenant = tenants[0];
  const landlords = Array.isArray(property?.landlords) && property.landlords.length ? property.landlords : property?.landlord ? [property.landlord] : [];
  const landlord = landlords[0];
  const isLandlord = userRole === 'landlord';
  const alertsHeld = isAlertsOnHold(property, (property?.landlords || []).filter((l) => l?.disabled).map((l) => l.id));

  const status = (property?.status || '').toLowerCase();
  const statusPill = status === 'occupied'
    ? { bg: isDarkMode ? 'rgba(82,183,136,0.16)' : '#DCEBE2', fg: isDarkMode ? t.green : '#1F4D38', dot: { borderRadius: '50%', bgcolor: t.green }, label: 'Let · tenancy active' }
    : status === 'vacant'
      ? { bg: isDarkMode ? 'rgba(214,158,46,0.16)' : '#F6E7C8', fg: isDarkMode ? t.amberText : '#6E4700', dot: { borderRadius: '50%', border: `2px solid ${t.amber}` }, label: 'Vacant' }
      : { bg: t.lineSoft, fg: t.body, dot: { borderRadius: '50%', bgcolor: t.muted }, label: property?.status ? property.status.charAt(0).toUpperCase() + property.status.slice(1) : 'Status unknown' };
  const urgentCerts = certRows.filter((r) => r.s.tone === 'urgent');
  const soonCert = certRows.filter((r) => r.s.tone === 'soon').sort((a, b) => (a.s.days ?? 1e9) - (b.s.days ?? 1e9))[0];
  const urgentJobs = openJobs.filter((m) => ['urgent', 'high'].includes((m.priorityLevel || m.priority || '').toLowerCase()));

  const money = rentSummary(tenant);
  const arrears = (payments || []).filter((p) => p.status === 'arrears').reduce((sum, p) => sum + amount(p.amount), 0);

  const eyebrowParts = ['Property', property?.postcode, property?.type].filter(Boolean);
  const heroBtn = { ...s.outlineBtn, height: 48, px: 2.25, fontSize: 15, borderRadius: '12px', bgcolor: t.surfaceAlt };
  const menuAction = (fn) => () => { setMenuEl(null); fn(); };

  return (
    <>
      <Box component="section" sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', lg: 'flex-end' }, gap: { xs: 3, lg: 4 }, pt: { xs: 1, md: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, minWidth: 0 }}>
          <Box sx={{ ...s.eyebrow, fontSize: 12, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <ButtonBase onClick={onBack} sx={{ ...s.eyebrow, fontSize: 12, borderRadius: '4px', '&:hover': { color: t.ink }, ...s.focusRing }}>Properties</ButtonBase>
            <Box component="span" aria-hidden sx={{ ...s.eyebrow, fontSize: 12 }}>/</Box>
            <Box component="span" sx={{ ...s.eyebrow, fontSize: 12 }}>{eyebrowParts.join(' · ')}</Box>
          </Box>
          <Box component="h1" sx={{ m: 0, fontFamily: SERIF, fontWeight: 400, fontSize: 'clamp(40px, 6vw, 80px)', lineHeight: 0.92, letterSpacing: '-0.02em', color: t.ink, overflowWrap: 'anywhere' }}>
            {safeFormatAddress(property?.address) || 'Property'}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Pill bg={statusPill.bg} fg={statusPill.fg} dot={statusPill.dot}>{statusPill.label}</Pill>
            {alertsHeld && (
              <Pill bg={isDarkMode ? 'rgba(214,158,46,0.16)' : '#F6E7C8'} fg={isDarkMode ? t.amberText : '#6E4700'} dot={{ borderRadius: '50%', border: `2px solid ${t.amber}` }}>
                Disabled
              </Pill>
            )}
            {urgentCerts.length > 0 ? (
              <Pill bg={isDarkMode ? 'rgba(240,113,74,0.16)' : '#F5DDD3'} fg={isDarkMode ? t.rust : '#8A2A0B'} dot={{ borderRadius: '2px', bgcolor: t.rust }}>
                {urgentCerts.length} compliance issue{urgentCerts.length === 1 ? '' : 's'}
              </Pill>
            ) : soonCert && (
              <Pill bg={isDarkMode ? 'rgba(214,158,46,0.16)' : '#F6E7C8'} fg={isDarkMode ? t.amberText : '#6E4700'} dot={{ borderRadius: '50%', border: `2px solid ${t.amber}` }}>
                {soonCert.c.name} {soonCert.s.status.toLowerCase()}
              </Pill>
            )}
            {openJobs.length > 0 && (
              <Pill
                bg={urgentJobs.length ? (isDarkMode ? 'rgba(240,113,74,0.16)' : '#F5DDD3') : t.lineSoft}
                fg={urgentJobs.length ? (isDarkMode ? t.rust : '#8A2A0B') : t.body}
                dot={{ borderRadius: '2px', bgcolor: urgentJobs.length ? t.rust : t.navy }}
              >
                {urgentJobs.length ? `${urgentJobs.length} urgent job${urgentJobs.length === 1 ? '' : 's'}` : `${openJobs.length} open job${openJobs.length === 1 ? '' : 's'}`}
              </Pill>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
          {!isLandlord && tenant?.email && <ButtonBase component="a" href={`mailto:${tenant.email}`} sx={heroBtn}>Message tenant</ButtonBase>}
          <ButtonBase onClick={onAddNote} sx={heroBtn}>Add note</ButtonBase>
          <ButtonBase onClick={onLogRepair} sx={{ ...heroBtn, border: 0, bgcolor: t.navy, color: isDarkMode ? t.panel : '#FFFFFF', '&:hover': { bgcolor: t.navy, opacity: 0.92 } }}>Log a repair</ButtonBase>
          <ButtonBase aria-label="More actions" aria-haspopup="menu" aria-expanded={Boolean(menuEl)} onClick={(e) => setMenuEl(e.currentTarget)} sx={{ width: 44, height: 44, borderRadius: '10px', color: t.ink, ...s.focusRing }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" /></svg>
          </ButtonBase>
          <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={() => setMenuEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { mt: 1, borderRadius: '12px', border: `1px solid ${t.line}`, bgcolor: t.surface, color: t.ink, fontFamily: SANS } }}>
            {!isLandlord && onToggleHold && (
              <MenuItem
                disabled={alertsHeld && property?.disabled !== true}
                onClick={menuAction(onToggleHold)}
              >
                {property?.disabled ? 'Enable' : alertsHeld ? 'Disabled' : 'Disable'}
              </MenuItem>
            )}
            <MenuItem onClick={menuAction(onEdit)}>Edit property</MenuItem>
            <MenuItem onClick={menuAction(onCompliance)}>Compliance check</MenuItem>
            <MenuItem onClick={menuAction(onBack)}>Back to properties</MenuItem>
          </Menu>
        </Box>
      </Box>

      {attention.length > 0 && (
        <Box component="section" aria-label="Needs attention" sx={{ bgcolor: t.panel, color: t.panelText, borderRadius: '18px', p: { xs: 2.5, md: '22px 24px' }, display: 'grid', gridTemplateColumns: { xs: '1fr', md: `220px repeat(${Math.min(attention.length, 2)}, minmax(0, 1fr))` }, gap: 3, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box component="span" sx={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.12em', color: t.accent }}>NEEDS YOU</Box>
            <Box component="span" sx={{ fontFamily: SERIF, fontSize: 30, lineHeight: 1 }}>
              {attention.length === 1 ? 'One thing to do here' : `${attention.length} things to do here`}
            </Box>
          </Box>
          {attention.slice(0, 2).map((a, i) => (
            <Box key={a.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, pl: { md: 3 }, pt: { xs: 2, md: 0 }, borderLeft: { md: `1px solid ${t.panelLine}` }, borderTop: { xs: `1px solid ${t.panelLine}`, md: 0 } }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, minWidth: 0 }}>
                <Box component="span" sx={{ fontSize: 15, fontWeight: 600 }}>{a.title}</Box>
                <Box component="span" sx={{ fontSize: 13, color: t.panelMuted }}>{a.meta}</Box>
              </Box>
              {a.action && (
                <ButtonBase onClick={() => onAttention(a)} sx={{ minHeight: 44, px: 2, borderRadius: '10px', fontFamily: SANS, fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0, ...(i === 0 ? { bgcolor: t.panelText, color: t.panel } : { border: `1px solid ${t.muted}`, color: t.panelText }), '&.Mui-focusVisible': { outline: `2px solid ${t.panelText}`, outlineOffset: '2px' } }}>
                  {a.action}
                </ButtonBase>
              )}
            </Box>
          ))}
        </Box>
      )}

      <Box component="section" sx={{ ...s.card, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(auto-fit, minmax(280px, 1fr))' }, columnGap: 0, overflow: 'hidden' }}>
        {!isLandlord && (
          <PersonCard
            s={s}
            role="Tenant"
            link={tenant?.id ? 'Open profile' : null}
            onLink={() => onOpenTenant(tenant)}
            name={tenant ? personName(tenant) || 'Unnamed tenant' : 'No tenant'}
            meta={tenant ? (tenants.length > 1 ? `Lead tenant · ${tenants.length} tenants` : 'Current tenant') : 'Property has no current tenant'}
            facts={[['Phone', tenant?.phone || '—'], ['Email', tenant?.email || '—']]}
            actions={[
              { label: 'Call', href: tenant?.phone ? `tel:${tenant.phone}` : null },
              { label: 'Message', href: tenant?.email ? `mailto:${tenant.email}` : null },
            ]}
          />
        )}
        <PersonCard
          s={s}
          role={landlords.length > 1 ? `Landlords (${landlords.length})` : 'Landlord'}
          link={landlord?.id ? 'Open profile' : null}
          onLink={() => onOpenLandlord(landlord)}
          name={landlord ? personName(landlord) || 'Unknown landlord' : 'No landlord'}
          meta={landlord?.address || (landlord ? 'Owner' : 'No landlord linked')}
          facts={[['Phone', landlord?.phone || '—'], ['Email', landlord?.email || '—']]}
          actions={[
            { label: 'Call', href: landlord?.phone ? `tel:${landlord.phone}` : null },
            { label: 'Email', href: landlord?.email ? `mailto:${landlord.email}` : null },
          ]}
        />
        <PersonCard
          s={s}
          role="Money"
          link={financeEnabled ? 'Statement' : null}
          onLink={onViewPayments}
          name={money.rent ? `${formatCurrency(money.rent)} pcm` : 'No rent set'}
          meta={tenant ? money.dueLabel : 'No current tenant'}
          facts={[
            ['Deposit', money.deposit],
            financeEnabled ? ['Arrears', arrears ? formatCurrency(arrears) : 'None'] : ['Tenancy', money.tenancy],
          ]}
          actions={financeEnabled ? [
            { label: 'Record payment', onClick: onRecordPayment },
            { label: 'All payments', onClick: onViewPayments },
          ] : [
            { label: 'Tenancy', onClick: () => onTab('tenancy') },
            { label: 'Open tenant', onClick: tenant?.id ? () => onOpenTenant(tenant) : null },
          ]}
        />
      </Box>

      <Box
        role="tablist"
        aria-label="Property sections"
        sx={{
          display: 'flex',
          flexWrap: 'nowrap',
          gap: { xs: 2.5, md: 3.5 },
          width: '100%',
          minWidth: 0,
          borderBottom: `1px solid ${t.line}`,
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehaviorX: 'contain',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {tabs.map((x) => {
          const on = x.id === tab;
          return (
            <ButtonBase key={x.id} role="tab" id={`record-tab-${x.id}`} aria-selected={on} aria-controls="record-tabpanel" onClick={() => onTab(x.id)}
              sx={{ minHeight: 48, flexShrink: 0, gap: 1, whiteSpace: 'nowrap', fontFamily: SANS, fontSize: 15, fontWeight: 600, color: on ? t.ink : t.muted, borderBottom: `3px solid ${on ? t.ink : 'transparent'}`, mb: '-1px', '&:hover': { color: t.ink }, ...s.focusRing }}>
              {x.label}
              {!!x.badge && <Box component="span" sx={{ fontFamily: MONO, fontSize: 12, color: t.rust }}>{x.badge}</Box>}
            </ButtonBase>
          );
        })}
      </Box>
    </>
  );
}

export function PropertyComplianceCard({ isDarkMode, certRows, onOpen, onView, onEdit, onDownload, onUpload }) {
  const s = recordStyles(isDarkMode);
  const { t } = s;
  const [menu, setMenu] = useState(null);
  const closeMenu = () => setMenu(null);
  // Fixed tracks only for aligned columns — name is the sole flexible column.
  const cols = {
    xs: '12px minmax(0, 1fr)',
    md: '12px minmax(0, 1fr) 168px 200px 220px',
  };
  const actionBtn = {
    ...s.underlineLink,
    minHeight: 32,
    fontSize: 13,
    whiteSpace: 'nowrap',
  };
  const cert = menu?.cert;
  const hasMenuFile = Boolean(cert?.url);
  return (
    <>
    <Box component="section" aria-label="Compliance" sx={{ ...s.card, overflow: 'hidden', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, px: 3, pt: 2.75, pb: 1.5 }}>
        <Box component="h2" sx={s.h2}>Compliance</Box>
        <ButtonBase onClick={onOpen} sx={{ ...s.underlineLink, flexShrink: 0 }}>Upload certificate</ButtonBase>
      </Box>
      <Box sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: cols.md, columnGap: 2, alignItems: 'end', px: 3, pb: 1 }}>
        <Box aria-hidden />
        <Box aria-hidden />
        <Box component="span" sx={s.eyebrow}>Expiry</Box>
        <Box component="span" sx={s.eyebrow}>Status</Box>
        <Box component="span" sx={{ ...s.eyebrow, textAlign: 'right' }}>Actions</Box>
      </Box>
      {certRows.length === 0 && (
        <Box sx={{ px: 3, py: 2.5, borderTop: `1px solid ${t.lineSoft}`, fontSize: 14, color: t.muted }}>No certificates tracked yet.</Box>
      )}
      {certRows.map(({ c, s: st }) => {
        const m = toneMark(t, st.tone);
        const hasFile = Boolean(c.url);
        return (
          <Box key={c.id} sx={{ display: 'grid', gridTemplateColumns: cols, columnGap: 2, rowGap: 1, alignItems: 'center', px: 3, py: { xs: 1.5, md: 1.25 }, minHeight: 60, borderTop: `1px solid ${t.lineSoft}` }}>
            <Box component="span" aria-hidden sx={{ width: 12, height: 12, boxSizing: 'border-box', borderRadius: m.radius, bgcolor: m.fill, border: `2px solid ${m.stroke}` }} />
            <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
              <Box component="span" sx={{ fontSize: 15, fontWeight: 600 }}>{c.name}</Box>
              <Box component="span" sx={{ display: { xs: 'block', md: 'none' }, fontSize: 13, fontWeight: 600, color: m.fg }}>{st.status}</Box>
            </Box>
            <Box component="span" sx={{ display: { xs: 'none', md: 'block' }, fontFamily: MONO, fontSize: 13, color: t.body }}>{st.date}</Box>
            <Box component="span" sx={{ display: { xs: 'none', md: 'block' }, fontSize: 13, fontWeight: 600, color: m.fg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{st.status}</Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: { md: 'flex-end' }, gap: { xs: 1, md: 1.5 }, gridColumn: { xs: '1 / -1', md: 'auto' }, pl: { xs: '20px', md: 0 }, minWidth: 0 }}>
              <ButtonBase disabled={!hasFile} onClick={() => hasFile && onView?.(c)} sx={actionBtn}>View</ButtonBase>
              <ButtonBase disabled={!hasFile} onClick={() => hasFile && onDownload?.(c)} sx={actionBtn}>Download</ButtonBase>
              <ButtonBase
                aria-label={`More actions for ${c.name}`}
                aria-haspopup="menu"
                aria-expanded={menu?.cert?.id === c.id}
                onClick={(e) => { e.stopPropagation(); setMenu({ el: e.currentTarget, cert: c }); }}
                sx={{ ...actionBtn, width: 32, minWidth: 32, textDecoration: 'none' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <circle cx="12" cy="5" r="2" />
                  <circle cx="12" cy="12" r="2" />
                  <circle cx="12" cy="19" r="2" />
                </svg>
              </ButtonBase>
            </Box>
          </Box>
        );
      })}
    </Box>
    <Menu
        anchorEl={menu?.el}
        open={Boolean(menu)}
        onClose={closeMenu}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 0.5, borderRadius: '12px', border: `1px solid ${t.line}`, bgcolor: t.surface, color: t.ink, fontFamily: SANS, zIndex: 1600 } }}
        style={{ zIndex: 1600 }}
      >
        <MenuItem disabled={!hasMenuFile} onClick={() => { closeMenu(); if (hasMenuFile) onEdit?.(cert); }}>Edit</MenuItem>
        <MenuItem onClick={() => { closeMenu(); if (cert) onUpload?.(cert); }}>Upload new</MenuItem>
      </Menu>
    </>
  );
}

/** Loading placeholder matching PropertyRecordHeader + overview cards. */
export function PropertyRecordSkeleton({ isDarkMode }) {
  const s = recordStyles(isDarkMode);
  const { t } = s;
  const pulse = {
    bgcolor: t.lineSoft,
    animation: 'pulse 1.5s ease-in-out infinite',
    '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
  };
  const bar = (w, h = 12, extra = {}) => (
    <Box sx={{ width: w, height: h, borderRadius: '6px', ...pulse, ...extra }} />
  );
  return (
    <Box
      className="property-details-page"
      aria-busy="true"
      aria-label="Loading property"
      sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}
    >
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', lg: 'flex-end' }, gap: { xs: 3, lg: 4 }, pt: { xs: 1, md: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, minWidth: 0, flex: 1 }}>
          {bar('40%', 12)}
          {bar({ xs: '90%', md: '70%' }, { xs: 44, md: 64 })}
          <Box sx={{ display: 'flex', gap: 1 }}>{bar(120, 32, { borderRadius: '16px' })}{bar(140, 32, { borderRadius: '16px' })}</Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {bar(140, 48, { borderRadius: '12px' })}
          {bar(100, 48, { borderRadius: '12px' })}
          {bar(120, 48, { borderRadius: '12px' })}
        </Box>
      </Box>

      <Box sx={{ ...s.card, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, columnGap: 0, overflow: 'hidden' }}>
        {[0, 1, 2].map((i) => (
          <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: { xs: 2.25, md: 2.5 }, px: { xs: 2.5, md: 2.75 }, borderRight: { md: i < 2 ? `1px solid ${t.line}` : 0 }, borderBottom: { xs: i < 2 ? `1px solid ${t.line}` : 0, md: 0 } }}>
            {bar(64, 11)}
            {bar('70%', 28)}
            {bar('45%', 13)}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, pt: 1.75, borderTop: `1px solid ${t.lineSoft}` }}>
              {bar('80%', 28)}{bar('80%', 28)}
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 3.5, borderBottom: `1px solid ${t.line}`, pb: 1.5 }}>
        {['Overview', 'Tenancy', 'Compliance', 'Maintenance', 'Documents'].map((label) => (
          <Box key={label} sx={{ height: 18, width: label.length * 9, borderRadius: '4px', ...pulse }} />
        ))}
      </Box>

      <Box sx={{ ...s.card, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 2.75, pb: 1.5 }}>
          {bar(160, 32)}
          {bar(120, 14)}
        </Box>
        {[0, 1, 2, 3, 4].map((i) => (
          <Box key={i} sx={{ display: 'grid', gridTemplateColumns: { xs: '12px 1fr', md: '12px 1fr 168px 200px 220px' }, columnGap: 2, alignItems: 'center', px: 3, py: 1.5, minHeight: 60, borderTop: `1px solid ${t.lineSoft}` }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', ...pulse }} />
            {bar('50%', 15)}
            {bar(100, 13, { display: { xs: 'none', md: 'block' } })}
            {bar(80, 13, { display: { xs: 'none', md: 'block' } })}
            {bar(140, 13, { display: { xs: 'none', md: 'block' }, justifySelf: 'end' })}
          </Box>
        ))}
      </Box>

      <Box sx={{ ...s.card, overflow: 'hidden' }}>
        <Box sx={{ px: 3, pt: 2.75, pb: 1.5 }}>{bar(140, 32)}</Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, columnGap: 4, px: 3, pb: 2 }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Box key={i} sx={{ py: 2, borderTop: `1px solid ${t.lineSoft}`, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {bar(72, 11)}
              {bar('60%', 20)}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export function PropertyOpenJobCard({ isDarkMode, job, onOpen, onAll }) {
  const s = recordStyles(isDarkMode);
  const { t } = s;
  const priority = (job.priorityLevel || job.priority || '').toLowerCase();
  const created = job.createdAt?.toDate ? job.createdAt.toDate() : new Date(job.createdAt);
  const daysOpen = isNaN(created) ? null : Math.max(0, Math.floor((Date.now() - created) / 86400000));
  const hot = priority === 'urgent' || priority === 'high';
  return (
    <Box component="section" aria-label="Open job" sx={{ ...s.card, p: '22px 24px', display: 'flex', flexDirection: 'column', gap: 2.25 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
        <Box component="h2" sx={s.h2}>Open job</Box>
        <ButtonBase onClick={onAll} sx={s.underlineLink}>All jobs for this property →</ButtonBase>
      </Box>
      <ButtonBase onClick={onOpen} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, textAlign: 'left', borderRadius: '8px', fontFamily: SANS, color: t.ink, ...s.focusRing }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
          <Box component="span" sx={{ fontSize: 18, fontWeight: 600 }}>{job.title || 'Repair'}</Box>
          <Box component="span" sx={{ fontSize: 13, color: t.muted }}>
            {[job.category, job.priority || job.priorityLevel ? `Priority: ${job.priorityLevel || job.priority}` : null, job.location].filter(Boolean).join(' · ') || 'Maintenance request'}
          </Box>
        </Box>
        {daysOpen != null && (
          <Box component="span" sx={{ minHeight: 28, display: 'inline-flex', alignItems: 'center', px: 1.25, borderRadius: '14px', flexShrink: 0, bgcolor: hot ? (isDarkMode ? 'rgba(240,113,74,0.16)' : '#F5DDD3') : t.lineSoft, color: hot ? (isDarkMode ? t.rust : '#8A2A0B') : t.body, fontSize: 12, fontWeight: 700 }}>
            {daysOpen} DAY{daysOpen === 1 ? '' : 'S'} OPEN
          </Box>
        )}
      </ButtonBase>
      <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none', display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 0.75 }}>
        {jobSteps(job).map((st) => (
          <Box component="li" key={st.label} aria-current={st.current ? 'step' : undefined} sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
            <Box component="span" sx={{ height: 6, borderRadius: '3px', bgcolor: st.done ? t.ink : st.current ? t.rust : t.lineSoft }} />
            <Box component="span" sx={{ fontSize: 13, fontWeight: 600, color: st.done || st.current ? t.ink : t.muted }}>{st.label}</Box>
            <Box component="span" sx={{ fontFamily: MONO, fontSize: 11, color: t.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{st.date}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}